// Appointment Request Service
// Handles middleman appointment requests for businesses NOT on BookAPT
// Includes natural invitation to join platform

import SimpleStorage from '../utils/simpleStorage';
import { smsService } from './smsService';
import { businessInvitationService } from './businessInvitationService';
import { phoneValidationService } from './phoneValidationService';
import { multiChannelService } from './multiChannelService';
import { Platform } from 'react-native';

export interface AppointmentRequest {
  id: string;
  userId: string;
  userName: string;
  userPhone: string;
  userEmail: string;
  businessName: string;
  businessPhone: string;
  businessEmail?: string;
  service: string;
  preferredDate: string;
  preferredTime: string;
  specialRequests?: string;
  /** Transportation quote / ride request (optional). */
  pickupAddress?: string;
  dropoffAddress?: string;
  status: 'pending' | 'confirmed' | 'declined' | 'business_will_call' | 'reschedule_requested' | 'expired' | 'cancelled';
  businessResponse?: string;
  suggestedTime?: string;
  invitationId?: string; // Link to business invitation
  createdAt: string;
  updatedAt: string;
}

class AppointmentRequestService {
  private storageKey = 'appointment_requests';
  private businessRequestCountsKey = 'business_request_counts';
  private readonly MAX_FREE_REQUESTS = 10; // Cap: Max 10 free appointment requests per business

  /**
   * Get request count for a business
   */
  private async getBusinessRequestCount(businessPhone: string): Promise<number> {
    try {
      const counts = await this.getBusinessRequestCounts();
      return counts[businessPhone] || 0;
    } catch (error) {
      console.error('Error getting business request count:', error);
      return 0;
    }
  }

  /**
   * Increment request count for a business (Firestore + local storage)
   */
  private async incrementBusinessRequestCount(businessPhone: string): Promise<number> {
    try {
      const counts = await this.getBusinessRequestCounts();
      counts[businessPhone] = (counts[businessPhone] || 0) + 1;
      
      // Save to local storage
      await SimpleStorage.setItem(this.businessRequestCountsKey, JSON.stringify(counts));
      
      // Also save to Firestore (web + mobile) so limits stay in sync
      if (Platform.OS === 'web') {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            await setDoc(doc(db, 'business_request_counts', 'all'), {
              counts: counts,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentRequestService: Business request count updated in Firestore`);
          }
        } catch (firestoreError: any) {
          console.error('❌ AppointmentRequestService: Error updating count in Firestore:', firestoreError);
          // Don't throw - local save succeeded
        }
      } else {
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              await getFirestore().collection('business_request_counts').doc('all').set({
                counts: counts,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ AppointmentRequestService: Business request count updated in Firestore (mobile)`);
            }
          }
        } catch (mobileErr: any) {
          console.error('❌ AppointmentRequestService: Error updating count in Firestore (mobile):', mobileErr?.message);
        }
      }
      
      return counts[businessPhone];
    } catch (error) {
      console.error('Error incrementing business request count:', error);
      return 1;
    }
  }

  /**
   * Get all business request counts (Firestore first, then local fallback)
   */
  private async getBusinessRequestCounts(): Promise<{ [businessPhone: string]: number }> {
    try {
      // Try to load from Firestore first
      let loadedCounts: { [businessPhone: string]: number } = {};
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            const countsDoc = await getDoc(doc(db, 'business_request_counts', 'all'));
            if (countsDoc.exists()) {
              loadedCounts = countsDoc.data().counts || {};
              loadedFromFirestore = true;
              console.log(`✅ AppointmentRequestService: Loaded business request counts from Firestore`);
              
              // Sync to local storage
              await SimpleStorage.setItem(this.businessRequestCountsKey, JSON.stringify(loadedCounts));
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  AppointmentRequestService: Could not load counts from Firestore, trying local storage:', firestoreError);
        }
      } else {
        // Mobile: load from Firestore (same as web) so limits are consistent
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              const firestore = getFirestore();
              const docRef = firestore.collection('business_request_counts').doc('all');
              const snap = await docRef.get();
              if (snap.exists) {
                const data = snap.data();
                if (data && typeof data.counts === 'object') {
                  loadedCounts = data.counts as { [businessPhone: string]: number };
                  loadedFromFirestore = true;
                  console.log(`✅ AppointmentRequestService: Loaded business request counts from Firestore (mobile)`);
                  await SimpleStorage.setItem(this.businessRequestCountsKey, JSON.stringify(loadedCounts));
                }
              }
            }
          }
        } catch (mobileErr: any) {
          console.warn('⚠️  AppointmentRequestService: Could not load counts from Firestore (mobile), trying local:', mobileErr?.message);
        }
      }
      
      // Fallback to local storage
      if (!loadedFromFirestore) {
        const data = await SimpleStorage.getItem(this.businessRequestCountsKey);
        if (data) {
          loadedCounts = JSON.parse(data);
        }
      }
      
      return loadedCounts;
    } catch (error) {
      console.error('Error getting business request counts:', error);
      return {};
    }
  }

  /**
   * Check if business has reached the cap
   */
  async hasReachedCap(businessPhone: string): Promise<boolean> {
    const count = await this.getBusinessRequestCount(businessPhone);
    return count >= this.MAX_FREE_REQUESTS;
  }

  /**
   * Create new appointment request
   * Also creates business invitation automatically
   */
  async createRequest(request: {
    userId: string;
    userName: string;
    userPhone: string;
    userEmail: string;
    businessName: string;
    businessPhone: string;
    businessEmail?: string;
    service: string;
    preferredDate: string;
    preferredTime: string;
    specialRequests?: string;
    businessCategory?: string;
    businessLocation?: string;
    zipcode?: string;
    pickupAddress?: string;
    dropoffAddress?: string;
  }): Promise<{ success: boolean; message: string; requestId?: string; reachedCap?: boolean; phoneNotSmsCapable?: boolean; validationResult?: any }> {
    try {
      console.log('📝 Creating appointment request for:', request.businessName);

      // Validate phone number and check if it's SMS-capable
      if (request.businessPhone) {
        console.log('🔍 Validating business phone number:', request.businessPhone);
        const validationResult = await phoneValidationService.validatePhoneNumber(request.businessPhone);
        
        if (!validationResult.isValid) {
          return {
            success: false,
            message: phoneValidationService.getErrorMessage(validationResult),
            validationResult,
          };
        }
        
        if (!validationResult.isSmsCapable) {
          console.warn('⚠️ Business phone number is not SMS-capable:', {
            phone: request.businessPhone,
            carrierType: validationResult.carrierType,
            carrierName: validationResult.carrierName,
          });
          
          return {
            success: false,
            message: phoneValidationService.getErrorMessage(validationResult) + 
              (request.businessEmail 
                ? `\n\nWould you like to send an email invitation instead?`
                : `\n\nPlease contact ${request.businessName} directly at ${request.businessPhone} to book your appointment.`),
            phoneNotSmsCapable: true,
            validationResult,
          };
        }
        
        console.log('✅ Phone number validated and SMS-capable:', {
          phone: validationResult.phoneNumber,
          carrierType: validationResult.carrierType,
          carrierName: validationResult.carrierName,
        });
      }

      // Check if business has reached the cap
      const currentCount = await this.getBusinessRequestCount(request.businessPhone);
      const reachedCap = currentCount >= this.MAX_FREE_REQUESTS;

      if (reachedCap) {
        return {
          success: false,
          message: `We've already sent ${this.MAX_FREE_REQUESTS} appointment requests to ${request.businessName}. We'd love to send more, but we've reached our free request limit. Would you like to invite them to join BookAPT (it's free for businesses to join)?`,
          reachedCap: true,
        };
      }

      // 1. Create appointment request record
      const appointmentRequest: AppointmentRequest = {
        id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: request.userId,
        userName: request.userName,
        userPhone: request.userPhone,
        userEmail: request.userEmail,
        businessName: request.businessName,
        businessPhone: request.businessPhone,
        businessEmail: request.businessEmail,
        service: request.service,
        preferredDate: request.preferredDate,
        preferredTime: request.preferredTime,
        specialRequests: request.specialRequests,
        pickupAddress: request.pickupAddress?.trim() || undefined,
        dropoffAddress: request.dropoffAddress?.trim() || undefined,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // 2. Increment business request count
      const newCount = await this.incrementBusinessRequestCount(request.businessPhone);
      console.log(`📊 Business request count: ${newCount}/${this.MAX_FREE_REQUESTS}`);

      // 3. Create business invitation (for tracking and join link)
      const tripNote =
        request.pickupAddress?.trim() || request.dropoffAddress?.trim()
          ? ` | Pickup: ${(request.pickupAddress || '').trim()} | Drop-off: ${(request.dropoffAddress || '').trim()}`
          : '';
      const invitationResult = await businessInvitationService.requestBusiness({
        userId: request.userId,
        userName: request.userName,
        userEmail: request.userEmail,
        businessName: request.businessName,
        businessType: request.businessCategory || 'general',
        businessPhone: request.businessPhone,
        businessEmail: request.businessEmail,
        location: request.businessLocation || '',
        zipcode: request.zipcode || '',
        notes: `Appointment request: ${request.service} on ${request.preferredDate} at ${request.preferredTime}${tripNote}`,
      });

      if (invitationResult.invitationId) {
        appointmentRequest.invitationId = invitationResult.invitationId;
      }

      // 4. Save appointment request
      await this.saveRequest(appointmentRequest);

      // 5. Send appointment request via multi-channel service (SMS, Call, or Email)
      // This ensures no request is missed - uses best available channel
      const multiChannelResult = await multiChannelService.sendAppointmentRequest(appointmentRequest);
      
      if (!multiChannelResult.success) {
        // Fallback to direct SMS if multi-channel fails
        console.warn('⚠️ Multi-channel service failed, falling back to direct SMS');
        await this.sendRequestToBusiness(appointmentRequest, invitationResult.invitationId, newCount);
      } else {
        console.log(`✅ Appointment request sent via ${multiChannelResult.primaryChannel.toUpperCase()}`);
      }

      // 6. Send initial status update to user
      await this.sendStatusUpdate(appointmentRequest, 'sent');

      console.log('✅ Appointment request created:', appointmentRequest.id);

      return {
        success: true,
        message: `We've sent your appointment request to ${request.businessName}. We'll notify you when they respond!`,
        requestId: appointmentRequest.id,
      };
    } catch (error) {
      console.error('Error creating appointment request:', error);
      return {
        success: false,
        message: 'Sorry, we couldn\'t send your appointment request. Please try again.',
      };
    }
  }

  /**
   * Send appointment request + invitation to business
   */
  private async sendRequestToBusiness(
    request: AppointmentRequest,
    invitationId?: string,
    requestCount?: number
  ): Promise<void> {
    const count = requestCount || await this.getBusinessRequestCount(request.businessPhone);
    const message = this.generateCombinedMessage(request, invitationId, count);
    
    console.log('📤 Sending appointment request to:', request.businessPhone);
    console.log(`📊 Request count: ${count}/${this.MAX_FREE_REQUESTS}`);
    
    // Double-check SMS capability before sending (in case validation was skipped)
    const validationResult = await phoneValidationService.validatePhoneNumber(request.businessPhone);
    if (!validationResult.isSmsCapable) {
      throw new Error(`Cannot send SMS to ${request.businessPhone}: ${phoneValidationService.getErrorMessage(validationResult)}`);
    }
    
    await smsService.sendSMS(request.businessPhone, message);
  }

  /**
   * Generate combined message (appointment request + invitation + counter)
   */
  private generateCombinedMessage(
    request: AppointmentRequest,
    invitationId?: string,
    requestCount: number = 1
  ): string {
    const formattedDate = this.formatDate(request.preferredDate);
    const joinLink = invitationId 
      ? `https://bookapt.io/join?ref=${invitationId}`
      : 'https://bookapt.io/join';

    const remainingRequests = Math.max(0, this.MAX_FREE_REQUESTS - requestCount);
    const isLastRequest = requestCount >= this.MAX_FREE_REQUESTS;

    let message = `Hi ${request.businessName}! 👋\n\n`;
    message += `You have a new appointment request from BookAPT:\n\n`;
    message += `Customer: ${request.userName}\n`;
    message += `Phone: ${request.userPhone}\n`;
    message += `Service: ${request.service}\n`;
    message += `Date: ${formattedDate}\n`;
    message += `Time: ${request.preferredTime}\n`;
    if (request.pickupAddress?.trim()) {
      message += `Pickup: ${request.pickupAddress.trim()}\n`;
    }
    if (request.dropoffAddress?.trim()) {
      message += `Drop-off: ${request.dropoffAddress.trim()}\n`;
    }
    
    if (request.specialRequests) {
      message += `Notes: ${request.specialRequests}\n`;
    }
    
    message += `\nReply options:\n`;
    message += `✅ YES - Confirm appointment\n`;
    message += `❌ NO - Decline\n`;
    message += `📞 CALL - Call customer to discuss\n`;
    message += `🔄 RESCHEDULE - Suggest different time\n`;
    
    message += `\n---\n\n`;
    
    // Add friendly counter (no urgency, just informative)
    if (isLastRequest) {
      message += `📊 You've received ${requestCount} appointment request${requestCount > 1 ? 's' : ''} through BookAPT!\n`;
      message += `This is your last free request. Join BookAPT (it's free!) to get unlimited bookings!\n\n`;
    } else {
      message += `📊 You've received ${requestCount} appointment request${requestCount > 1 ? 's' : ''} through BookAPT.\n`;
      message += `${remainingRequests} more free request${remainingRequests !== 1 ? 's' : ''} remaining.\n\n`;
    }
    
    message += `💡 Join BookAPT - It's FREE for businesses!\n\n`;
    message += `✅ Get unlimited bookings automatically (no more SMS requests!)\n`;
    message += `✅ Customers book 24/7 via chat\n`;
    message += `✅ Reduce no-shows with automated reminders\n`;
    message += `✅ Get paid instantly through the app\n`;
    message += `✅ 100% FREE to join - No credit card required\n\n`;
    message += `Join for free: ${joinLink}\n\n`;
    message += `Powered by BookAPT`;

    return message;
  }

  /**
   * Process business response
   */
  async processBusinessResponse(
    requestId: string,
    response: 'YES' | 'NO' | 'CALL' | 'RESCHEDULE',
    suggestedTime?: string
  ): Promise<boolean> {
    try {
      const request = await this.getRequest(requestId);
      if (!request) {
        console.error('Request not found:', requestId);
        return false;
      }

      // Update status based on response
      switch (response) {
        case 'YES':
          request.status = 'confirmed';
          request.businessResponse = 'confirmed';
          break;
        case 'NO':
          request.status = 'declined';
          request.businessResponse = 'declined';
          break;
        case 'CALL':
          request.status = 'business_will_call';
          request.businessResponse = 'will_call';
          break;
        case 'RESCHEDULE':
          request.status = 'reschedule_requested';
          request.businessResponse = 'reschedule';
          request.suggestedTime = suggestedTime;
          break;
      }

      request.updatedAt = new Date().toISOString();
      await this.updateRequest(request);

      // Notify user of response
      await this.notifyUserOfResponse(request);

      console.log('✅ Processed business response:', response);
      return true;
    } catch (error) {
      console.error('Error processing business response:', error);
      return false;
    }
  }

  /**
   * Notify user of business response
   * Also triggers UI update event for real-time status updates
   */
  private async notifyUserOfResponse(request: AppointmentRequest): Promise<void> {
    let message = '';
    
    switch (request.status) {
      case 'confirmed':
        message = `✅ Great news! ${request.businessName} confirmed your appointment for ${request.service} on ${this.formatDate(request.preferredDate)} at ${request.preferredTime}.`;
        break;
      case 'declined':
        message = `❌ ${request.businessName} declined your appointment request. Would you like to try a different time or find another business?`;
        break;
      case 'business_will_call':
        message = `📞 ${request.businessName} will call you at ${request.userPhone} to discuss your appointment. Expect a call soon!`;
        break;
      case 'reschedule_requested':
        const suggested = request.suggestedTime || 'a different time';
        message = `🔄 ${request.businessName} suggests: ${suggested} instead. Would you like to accept this time?`;
        break;
    }

    // In production, send push notification or email
    console.log('📬 User notification:', message);
    
    // Send SMS to user
    if (message) {
      await smsService.sendSMS(request.userPhone, message);
    }

    // Send push notification to user
    try {
      const { pushNotificationService } = await import('./pushNotificationService');
      const userId = request.userId || request.userEmail; // Use email as fallback
      
      if (userId) {
        await pushNotificationService.sendNotificationToUser(
          userId,
          request.status === 'confirmed' 
            ? '✅ Appointment Confirmed!' 
            : request.status === 'declined'
            ? '❌ Appointment Declined'
            : '📞 Appointment Update',
          message,
          {
            type: 'appointment_response',
            requestId: request.id,
            status: request.status,
            businessName: request.businessName
          }
        );
        console.log('📱 Push notification sent to user');
      }
    } catch (error) {
      console.error('Error sending push notification to user:', error);
      // Don't throw - SMS is more important
    }

    // Trigger UI update event for real-time status updates
    // This will notify the app to refresh appointment list
    this.triggerStatusUpdate(request);
  }

  /**
   * Trigger status update event for real-time UI updates
   * Web only: CustomEvent + localStorage. Mobile uses polling in UserAppointmentsScreen.
   */
  private triggerStatusUpdate(request: AppointmentRequest): void {
    if (Platform.OS !== 'web') return;
    try {
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('appointmentStatusUpdate', {
          detail: {
            requestId: request.id,
            status: request.status,
            businessName: request.businessName,
            service: request.service,
          }
        }));
      }
      if (typeof localStorage !== 'undefined') {
        const updateKey = `appointment_update_${request.id}`;
        localStorage.setItem(updateKey, JSON.stringify({
          requestId: request.id,
          status: request.status,
          timestamp: new Date().toISOString(),
        }));
      }
    } catch (error) {
      console.error('Error storing status update:', error);
    }
  }

  /**
   * Send status update notification at each step
   */
  async sendStatusUpdate(
    request: AppointmentRequest,
    step: 'sent' | 'escalating' | 'following_up' | 'confirmed' | 'declined'
  ): Promise<void> {
    let message = '';
    
    switch (step) {
      case 'sent':
        message = `✅ We've sent your appointment request to ${request.businessName}. We'll notify you when they respond!`;
        break;
      case 'escalating':
        message = `⏳ We haven't heard back from ${request.businessName} yet. We're following up with them via phone call to ensure they see your request.`;
        break;
      case 'following_up':
        message = `📞 We're actively reaching out to ${request.businessName} through multiple channels. We'll update you as soon as we have news!`;
        break;
      case 'confirmed':
        message = `✅ Great news! ${request.businessName} confirmed your appointment for ${request.service} on ${this.formatDate(request.preferredDate)} at ${request.preferredTime}.`;
        break;
      case 'declined':
        message = `❌ ${request.businessName} declined your appointment request. Would you like to try a different time or find another business?`;
        break;
    }

    if (message) {
      console.log(`📬 Status update (${step}):`, message);
      await smsService.sendSMS(request.userPhone, message);
      this.triggerStatusUpdate(request);
    }
  }

  /**
   * Get request by ID
   */
  async getRequest(requestId: string): Promise<AppointmentRequest | null> {
    const requests = await this.getAllRequests();
    return requests.find(req => req.id === requestId) || null;
  }

  /**
   * Get all requests (Firestore first, then local fallback)
   */
  async getAllRequests(): Promise<AppointmentRequest[]> {
    try {
      // Try to load from Firestore first (most up-to-date)
      let loadedRequests: AppointmentRequest[] = [];
      let loadedFromFirestore = false;
      
      if (Platform.OS === 'web') {
        try {
          const { collection, getDocs } = await import('firebase/firestore');
          const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
          
          // Ensure Firebase is initialized before using it
          const initialized = initializeFirebase();
          if (!initialized) {
            console.warn('⚠️  AppointmentRequestService: Firebase not initialized, skipping Firestore load');
          } else {
            const db = getWebDb();
            // Validate that db is a valid Firestore instance
            if (db && typeof db === 'object') {
              try {
                const timeoutPromise = new Promise<AppointmentRequest[]>((resolve) =>
                  setTimeout(() => {
                    console.warn('⚠️  AppointmentRequestService: Firestore query timeout, using empty array');
                    resolve([]);
                  }, 3000)
                );
                const queryPromise = getDocs(collection(db, 'appointment_requests')).then(snapshot =>
                  snapshot.docs.map((doc: any) => ({
                    id: doc.id,
                    ...doc.data()
                  } as AppointmentRequest))
                );
                loadedRequests = await Promise.race([queryPromise, timeoutPromise]);
                if (loadedRequests.length > 0) {
                  loadedFromFirestore = true;
                  console.log(`✅ AppointmentRequestService: Loaded ${loadedRequests.length} requests from Firestore`);
                  
                  // Sync to local storage for offline access
                  await SimpleStorage.setItem(this.storageKey, JSON.stringify(loadedRequests));
                } else {
                  console.log('📋 AppointmentRequestService: No requests from Firestore (timeout or empty)');
                }
              } catch (collectionError: any) {
                // Handle collection errors specifically
                if (collectionError.message?.includes('Expected first argument to collection()') ||
                    collectionError.message?.includes('timeout') ||
                    collectionError.code === 'unavailable' ||
                    collectionError.message?.includes('offline')) {
                  console.warn('⚠️  AppointmentRequestService: Firestore unavailable or invalid, skipping Firestore load');
                } else {
                  // Don't throw - just log and continue to fallback
                  console.warn('⚠️  AppointmentRequestService: Firestore error:', collectionError.message);
                }
              }
            } else {
              console.warn('⚠️  AppointmentRequestService: No valid Firebase database instance found, skipping Firestore load');
            }
          }
        } catch (firestoreError: any) {
          if (!firestoreError.message?.includes('Expected first argument to collection()')) {
            console.warn('⚠️  AppointmentRequestService: Could not load requests from Firestore, trying local storage');
          }
        }
      } else {
        // Mobile: load from Firestore (same DB as web + backend webhook) so business replies work
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              const firestore = getFirestore();
              const snapshot = await firestore.collection('appointment_requests').get();
              loadedRequests = snapshot.docs.map((doc: any) => ({
                id: doc.id,
                ...doc.data()
              } as AppointmentRequest));
              if (loadedRequests.length > 0) {
                loadedFromFirestore = true;
                console.log(`✅ AppointmentRequestService: Loaded ${loadedRequests.length} requests from Firestore (mobile)`);
                await SimpleStorage.setItem(this.storageKey, JSON.stringify(loadedRequests));
              }
            }
          }
        } catch (mobileErr: any) {
          console.warn('⚠️  AppointmentRequestService: Mobile Firestore load failed:', mobileErr?.message);
        }
      }

      // Fallback to local storage if Firestore didn't work
      if (!loadedFromFirestore) {
        const data = await SimpleStorage.getItem(this.storageKey);
        if (data) {
          loadedRequests = JSON.parse(data);
          console.log(`📋 AppointmentRequestService: Loaded ${loadedRequests.length} requests from local storage`);
        }
      }
      
      return loadedRequests;
    } catch (error) {
      console.error('Error getting requests:', error);
      return [];
    }
  }

  /**
   * Get requests by user ID
   */
  async getRequestsByUser(userId: string): Promise<AppointmentRequest[]> {
    const allRequests = await this.getAllRequests();
    return allRequests.filter(req => req.userId === userId);
  }

  /**
   * Get requests by status
   */
  async getRequestsByStatus(status: AppointmentRequest['status']): Promise<AppointmentRequest[]> {
    const allRequests = await this.getAllRequests();
    return allRequests.filter(req => req.status === status);
  }

  /**
   * Save request (Firestore + local storage)
   */
  private async saveRequest(request: AppointmentRequest): Promise<void> {
    const requests = await this.getAllRequests();
    requests.push(request);
    
    // Save to local storage (for offline access)
    await SimpleStorage.setItem(this.storageKey, JSON.stringify(requests));
    
    // Also save to Firestore (web + mobile) so backend webhook can find request when business replies
    if (Platform.OS === 'web') {
      try {
        const { doc, setDoc } = await import('firebase/firestore');
        const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
        if (initializeFirebase()) {
          const db = getWebDb();
          if (db) {
            await setDoc(doc(db, 'appointment_requests', request.id), {
              ...request,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentRequestService: Request saved to Firestore: ${request.id}`);
          }
        }
      } catch (firestoreError: any) {
        console.warn('⚠️  AppointmentRequestService: Firestore save failed (web):', (firestoreError as any)?.message);
      }
    } else {
      try {
        const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
        if (initializeFirebase()) {
          const getFirestore = getMobileFirestore();
          if (getFirestore) {
            await getFirestore().collection('appointment_requests').doc(request.id).set({
              ...request,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentRequestService: Request saved to Firestore (mobile): ${request.id}`);
          }
        }
      } catch (mobileErr: any) {
        console.warn('⚠️  AppointmentRequestService: Firestore save failed (mobile):', mobileErr?.message);
      }
    }
  }

  /**
   * Update request (Firestore + local storage)
   */
  private async updateRequest(updatedRequest: AppointmentRequest): Promise<void> {
    const requests = await this.getAllRequests();
    const index = requests.findIndex(req => req.id === updatedRequest.id);
    
    if (index !== -1) {
      requests[index] = updatedRequest;
      
      // Save to local storage (for offline access)
      await SimpleStorage.setItem(this.storageKey, JSON.stringify(requests));
      
      // Also update in Firestore (web + mobile) so backend and other devices see updates
      if (Platform.OS === 'web') {
        try {
          const { doc, updateDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          if (db) {
            await updateDoc(doc(db, 'appointment_requests', updatedRequest.id), {
              ...updatedRequest,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentRequestService: Request updated in Firestore: ${updatedRequest.id}`);
          }
        } catch (e: any) {
          console.warn('⚠️  AppointmentRequestService: Firestore update failed (web):', e?.message);
        }
      } else {
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              await getFirestore().collection('appointment_requests').doc(updatedRequest.id).update({
                ...updatedRequest,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ AppointmentRequestService: Request updated in Firestore (mobile): ${updatedRequest.id}`);
            }
          }
        } catch (e: any) {
          console.warn('⚠️  AppointmentRequestService: Firestore update failed (mobile):', e?.message);
        }
      }
    }
  }

  /**
   * Format date for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Handle incoming SMS from business (for response processing)
   * This would be called by a webhook from Twilio
   */
  async handleBusinessSMSResponse(
    businessPhone: string,
    message: string
  ): Promise<void> {
    // Find pending request for this business
    const requests = await this.getAllRequests();
    const pendingRequest = requests.find(
      req => req.businessPhone === businessPhone && req.status === 'pending'
    );

    if (!pendingRequest) {
      console.log('No pending request found for:', businessPhone);
      return;
    }

    const response = message.toUpperCase().trim();

    if (response.includes('YES') || response.includes('CONFIRM') || response.includes('ACCEPT')) {
      await this.processBusinessResponse(pendingRequest.id, 'YES');
    } else if (response.includes('NO') || response.includes('DECLINE') || response.includes('REJECT')) {
      await this.processBusinessResponse(pendingRequest.id, 'NO');
    } else if (response.includes('CALL')) {
      await this.processBusinessResponse(pendingRequest.id, 'CALL');
    } else if (response.includes('RESCHEDULE') || response.includes('DIFFERENT') || response.includes('LATER')) {
      // Try to extract time from message
      const suggestedTime = this.extractTimeFromMessage(message);
      await this.processBusinessResponse(pendingRequest.id, 'RESCHEDULE', suggestedTime);
    }
  }

  /**
   * Extract time suggestion from message
   */
  private extractTimeFromMessage(message: string): string {
    // Simple extraction - in production, use NLP
    const timePattern = /(\d{1,2}):?(\d{2})?\s*(AM|PM|am|pm)/i;
    const match = message.match(timePattern);
    if (match) {
      return match[0];
    }
    return message; // Return full message if no time found
  }

  /**
   * Reset request count for a business (when they join BookAPT)
   */
  async resetBusinessRequestCount(businessPhone: string): Promise<void> {
    try {
      const counts = await this.getBusinessRequestCounts();
      delete counts[businessPhone];
      
      // Save to local storage
      await SimpleStorage.setItem(this.businessRequestCountsKey, JSON.stringify(counts));
      
      // Also update in Firestore (web + mobile)
      if (Platform.OS === 'web') {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const db = getWebDb();
          
          if (db) {
            await setDoc(doc(db, 'business_request_counts', 'all'), {
              counts: counts,
              updatedAt: new Date().toISOString(),
            });
            console.log(`✅ AppointmentRequestService: Business request count reset in Firestore`);
          }
        } catch (firestoreError: any) {
          console.error('❌ AppointmentRequestService: Error resetting count in Firestore:', firestoreError);
          // Don't throw - local save succeeded
        }
      } else {
        try {
          const { getMobileFirestore, initializeFirebase } = require('./unifiedAuthService');
          if (initializeFirebase()) {
            const getFirestore = getMobileFirestore();
            if (getFirestore) {
              await getFirestore().collection('business_request_counts').doc('all').set({
                counts: counts,
                updatedAt: new Date().toISOString(),
              });
              console.log(`✅ AppointmentRequestService: Business request count reset in Firestore (mobile)`);
            }
          }
        } catch (mobileErr: any) {
          console.error('❌ AppointmentRequestService: Error resetting count in Firestore (mobile):', mobileErr?.message);
        }
      }
      
      console.log('✅ Reset request count for business:', businessPhone);
    } catch (error) {
      console.error('Error resetting business request count:', error);
    }
  }

  /**
   * Get remaining free requests for a business
   */
  async getRemainingRequests(businessPhone: string): Promise<number> {
    const count = await this.getBusinessRequestCount(businessPhone);
    return Math.max(0, this.MAX_FREE_REQUESTS - count);
  }

  /**
   * Mark request as expired (no response after 48 hours)
   */
  async markExpiredRequests(): Promise<number> {
    const requests = await this.getAllRequests();
    const now = new Date();
    let expiredCount = 0;

    for (const request of requests) {
      if (request.status === 'pending') {
        const createdAt = new Date(request.createdAt);
        const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceCreation >= 48) {
          request.status = 'expired';
          request.updatedAt = new Date().toISOString();
          await this.updateRequest(request);
          
          // Notify user
          await smsService.sendSMS(
            request.userPhone,
            `⏰ We haven't heard back from ${request.businessName} yet. We'll follow up with them. In the meantime, here are similar businesses nearby...`
          );
          
          expiredCount++;
        }
      }
    }

    return expiredCount;
  }
}

export const appointmentRequestService = new AppointmentRequestService();
export default appointmentRequestService;

